import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Stethoscope, X, Play, Square, Check, AlertTriangle, Loader2 } from 'lucide-react';

import { BRAND } from '../../brand';
import { claimAiReview, claimAiReviewJob } from '../BaseURL';
import useDialogGuard from './dialogGuard';

// ============================================================================
//  ClinicalReviewDialog — watching a month be read, claim by claim
// ============================================================================
//
//  ASKED FOR: "If you allow the system to go through each claim by claim,
//   picking the medication, the procedure, it may take time. But give us a
//   progress bar where the software is able to go through each claim. Make it
//   very nice."
//
//  A month is 2,264 claims and each is a round trip to a model, so this runs for
//  minutes. Minutes of a spinner with no numbers is indistinguishable from a
//  hang, and an officer who cannot tell the difference will reload the page and
//  start it again — which on a metered API is a real cost, not just a nuisance.
//
//  So the screen answers four questions at all times, and they are the four an
//  officer actually asks:
//
//      how far through is it        the bar, and "412 of 2,264"
//      is it still moving           the claim currently being read, by name
//      has it found anything yet    the running count, live
//      can I stop it                yes, and what is done stays done
//
//  ── IT IS SAFE TO CLOSE ────────────────────────────────────────────────────
//
//  The scan is a JOB on the server, not work owned by this dialog. Closing the
//  panel — or the browser — does not stop it, and reopening picks the same job
//  back up by its id. The only thing that ends a run is Stop, and even that banks
//  every claim already judged.
//
//  ── WHY "WARNING" IS EVERYWHERE IN THIS FILE ───────────────────────────────
//
//  "This one should be in a form of a warning, because we cannot force the people
//   to say it is an error." The wording, the colour and the icon are all amber on
//   purpose: what comes back is a model's clinical opinion, which is a reason for
//   a person to look — not a verdict on the claim. Dressing it as an NHIA
//   rejection would be a lie about how much it knows.

const POLL_MS = 900;

//  Kept in sessionStorage so closing the dialog mid-run and coming back finds the
//  same job rather than offering to start a second one over the same month.
const JOB_KEY = 'mefims.clinicalReview.job';

const pct = (done, total) => (total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0);

const stat = (label, value, tone) => (
  <div style={{
    flex: '1 1 92px', minWidth: 92, padding: '9px 11px', borderRadius: 9,
    background: tone === 'warn' ? '#FFF8EC' : '#F8FAFC',
    border: `1px solid ${tone === 'warn' ? 'rgba(224,139,47,.30)' : BRAND.line}`,
  }}>
    <div style={{
      fontSize: 19, fontWeight: 900, lineHeight: 1.1,
      fontVariantNumeric: 'tabular-nums',
      color: tone === 'warn' ? '#8A5510' : BRAND.ink,
    }}>{Number(value || 0).toLocaleString()}</div>
    <div style={{ fontSize: 10.5, fontWeight: 700, color: BRAND.inkMuted, marginTop: 2 }}>
      {label}
    </div>
  </div>
);

export default function ClinicalReviewDialog({
  open, scope, monthLabel, reviewed, flagged, total, onClose, onDone, onShowFlagged,
}) {
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const timer = useRef(null);
  const { panelRef, backdropProps } = useDialogGuard();

  const running = job && (job.state === 'running' || job.state === 'starting');

  // ── Watching ──────────────────────────────────────────────────────────────
  //
  //  Polled rather than streamed. A long-lived SSE connection through whatever
  //  proxy a facility runs is a support problem waiting to happen, and a second
  //  of latency on a run measured in minutes is not worth it.
  //
  //  ── THE POLL IS ONE FUNCTION, HELD IN A REF ───────────────────────────────
  //
  //  THE BUG THIS FIXES, and it made the whole feature look dead: the recursive
  //  poll used to be defined INSIDE the effect that runs when the dialog opens.
  //  That effect had already run — finding no stored job, because none had been
  //  started yet — and it never ran again, because `open` had not changed. So
  //  pressing Start fired one lookup 250 ms later, showed "0 of 0" from the job's
  //  own starting state, and then never asked again. The scan was running on the
  //  server the whole time; the screen had simply stopped listening.
  //
  //  A ref rather than a useCallback because `tick` reschedules ITSELF, so it has
  //  to be reachable from inside its own timeout without being a dependency of
  //  the effect that created it.
  const tickRef = useRef(null);
  const aliveRef = useRef(true);

  tickRef.current = async (id) => {
    try {
      const { data } = await axios.get(`${claimAiReviewJob}/${id}`);
      if (!aliveRef.current) return;
      if (!data?.success) return;
      setJob(data.job);
      if (data.job.state === 'running' || data.job.state === 'starting') {
        timer.current = setTimeout(() => {
          if (tickRef.current) tickRef.current(id);
        }, POLL_MS);
      } else {
        //  Finished, cancelled or failed: the list behind this has new answers on
        //  it either way.
        try { sessionStorage.removeItem(JOB_KEY); } catch { /* private mode */ }
        if (onDone) onDone();
      }
    } catch {
      //  A job the server has forgotten — it keeps them for half an hour — is not
      //  an error worth shouting about. The officer can start another.
      if (!aliveRef.current) return;
      try { sessionStorage.removeItem(JOB_KEY); } catch { /* private mode */ }
      setJob(null);
    }
  };

  //  Reopening the dialog picks a running job back up rather than offering to
  //  start a second one over the same month.
  useEffect(() => {
    if (!open) return undefined;
    aliveRef.current = true;

    let stored = null;
    try { stored = sessionStorage.getItem(JOB_KEY); } catch { /* private mode */ }
    if (stored && tickRef.current) tickRef.current(stored);

    return () => {
      aliveRef.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const start = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await axios.post(claimAiReview, scope);
      if (!data?.success) throw new Error(data?.message || 'The review could not be started.');
      setJob(data.job);
      try { sessionStorage.setItem(JOB_KEY, data.job.id); } catch { /* private mode */ }
      //  START THE RECURRING POLL. This is the line whose absence made the dialog
      //  sit at "0 of 0" while the server worked. Quickly the first time, so the
      //  bar moves the moment the button is pressed — the job's own first report
      //  is `starting` with a total of nothing, and a second of that reads as a
      //  hang.
      aliveRef.current = true;
      const id = data.job.id;
      timer.current = setTimeout(() => {
        if (tickRef.current) tickRef.current(id);
      }, 250);
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally { setBusy(false); }
  };

  const stop = async () => {
    if (!job) return;
    setBusy(true);
    try { await axios.delete(`${claimAiReviewJob}/${job.id}`); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (!open && timer.current) clearTimeout(timer.current);
  }, [open]);

  if (!open) return null;

  const done = job?.done || 0;
  const jobTotal = job?.total || 0;
  const bar = pct(done, jobTotal);
  const neverRun = reviewed === 0 && !job;

  return (
    <div role="dialog" aria-modal="true" aria-label="Clinical review"
      {...backdropProps}
      style={{
        position: 'fixed', inset: 0, zIndex: 92, background: 'rgba(15,23,42,.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 16px',
      }}>
      <div ref={panelRef} style={{
        width: '100%', maxWidth: 640, background: '#fff', borderRadius: 12,
        border: `1px solid ${BRAND.line}`, boxShadow: '0 24px 60px rgba(15,23,42,.30)',
        maxHeight: 'calc(100vh - 96px)', display: 'flex', flexDirection: 'column',
      }}>

        {/* ── header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '13px 16px',
          borderBottom: `1px solid ${BRAND.line}`,
        }}>
          <Stethoscope size={17} style={{ color: '#8A5510', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: BRAND.ink }}>
              Clinical review
            </div>
            <div style={{ fontSize: 11.5, color: BRAND.inkMuted }}>
              {monthLabel} · {Number(total || 0).toLocaleString()} claims
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ border: 0, background: 'none', cursor: 'pointer',
              padding: 4, color: BRAND.inkMuted, display: 'flex' }}>
            <X size={16} />
          </button>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', display: 'grid', gap: 12 }}>

          {/* ── what it does, in the officer's terms ── */}
          <div style={{
            padding: '10px 12px', borderRadius: 9, fontSize: 12.5, lineHeight: 1.65,
            background: '#F8FAFC', border: `1px solid ${BRAND.line}`, color: BRAND.ink,
          }}>
            Every claim is read once and checked for two things:
            <div style={{ marginTop: 6, display: 'grid', gap: 4, fontSize: 12.2 }}>
              <div>· a <b>medicine or lab that does not fit the diagnosis</b> — malaria
                treated with something that is not an antimalarial, a test that relates
                to nothing on the claim</div>
              <div>· a <b>course length that is wrong for the condition</b> — more days
                than the condition is treated for, or fewer</div>
            </div>
            {/*  Said plainly and early. The whole feature turns on the officer
                 understanding that this advises and does not decide. */}
            <div style={{ marginTop: 8, color: '#8A5510', fontWeight: 700 }}>
              Everything it finds is a warning, never an error. Nothing is changed on
              any claim — it only marks them for you to look at.
            </div>
          </div>

          {error && (
            <div style={{
              padding: '9px 11px', borderRadius: 8, fontSize: 12,
              background: '#FEF2F2', color: '#B91C1C', border: '1px solid #fecaca',
            }}>{error}</div>
          )}

          {/* ── the bar ── */}
          {job && (
            <div>
              <div style={{
                display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
                marginBottom: 6, fontSize: 12,
              }}>
                <span style={{ fontWeight: 800, color: BRAND.ink }}>
                  {/*  THE FIRST PHASE HAS ITS OWN WORDS. Before the model is asked
                       anything the job has to fetch the month's claims, which on a
                       large one takes several seconds — and "Reading the claims…
                       0 of 0" during that is indistinguishable from a hang. It is
                       a different thing happening and it says so. */}
                  {jobTotal === 0 && (job.state === 'starting' || job.state === 'running')
                    ? 'Finding the claims in this month…'
                    : job.state === 'running' || job.state === 'starting'
                      ? 'Reading the claims…'
                      : job.state === 'cancelled' ? 'Stopped'
                        : job.state === 'failed' ? 'Could not finish' : 'Finished'}
                </span>
                <span style={{ color: BRAND.inkMuted, fontVariantNumeric: 'tabular-nums' }}>
                  {jobTotal === 0 && running
                    ? 'just a moment'
                    : <>{done.toLocaleString()} of {jobTotal.toLocaleString()} · {bar}%</>}
                </span>
              </div>
              <div style={{
                height: 9, borderRadius: 999, background: '#EEF2F7', overflow: 'hidden',
              }}>
                <div style={{
                  //  An indeterminate sliver while the month is being fetched, so
                  //  the bar is never a flat empty trough that reads as stuck.
                  width: jobTotal === 0 && running ? '12%' : `${bar}%`,
                  height: '100%', borderRadius: 999,
                  background: job.state === 'failed' ? '#B91C1C'
                    : job.flagged > 0 ? '#E08B2F' : BRAND.green,
                  opacity: jobTotal === 0 && running ? 0.55 : 1,
                  transition: 'width .3s ease',
                }} />
              </div>
              {/*  The claim being read RIGHT NOW. This is the line that makes a
                   long run legible: a number that moves proves progress, a name
                   that moves proves it is doing the thing it said. */}
              <div style={{
                marginTop: 6, fontSize: 11.5, color: BRAND.inkMuted,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                minHeight: 16,
              }}>
                {running && job.current
                  ? <><Loader2 size={11} className="spin" style={{ verticalAlign: -1 }} />
                    {' '}{job.current}</>
                  : job.error || ''}
              </div>
            </div>
          )}

          {/* ── the numbers ── */}
          {job && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {stat('flagged', job.flagged, 'warn')}
              {stat('warnings', job.findings, 'warn')}
              {/*  Skipped is not a failure and must not read as one: it is the
                   claims whose treatment has not changed since the last run, and
                   it is what makes running this twice cheap. */}
              {stat('unchanged', job.skipped)}
              {job.errors > 0 && stat('unreadable', job.errors, 'warn')}
            </div>
          )}

          {/* ── where it stands before anything is started ── */}
          {!job && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {stat('already reviewed', reviewed)}
              {stat('flagged', flagged, 'warn')}
              {stat('in this month', total)}
            </div>
          )}

          {neverRun && (
            <div style={{ fontSize: 12, color: BRAND.inkMuted, lineHeight: 1.6 }}>
              Nothing in this month has been reviewed yet. The first run reads every
              claim; later runs only re-read the ones whose diagnosis or treatment has
              changed since, so they finish in a fraction of the time.
            </div>
          )}

          {/*  The one honest thing to say about a finished run with no findings.
               "0 flagged" on its own could mean it found nothing OR that it never
               looked, and those are different facts. */}
          {job && !running && job.state === 'done' && job.flagged === 0 && (
            <div style={{
              padding: '10px 12px', borderRadius: 9, fontSize: 12.5,
              background: '#E7F5EC', color: '#1B6631', border: '1px solid #BFE3CC',
            }}>
              <Check size={13} style={{ verticalAlign: -2 }} /> Read {done.toLocaleString()}
              {' '}claim{done === 1 ? '' : 's'} and found nothing to question.
            </div>
          )}
        </div>

        {/* ── actions ── */}
        <div style={{
          display: 'flex', gap: 8, padding: '12px 16px', alignItems: 'center',
          flexWrap: 'wrap', borderTop: `1px solid ${BRAND.line}`,
        }}>
          <div style={{ flex: 1, minWidth: 140, fontSize: 11.5, color: BRAND.inkMuted }}>
            {running
              ? 'You can close this — the review keeps running.'
              : ''}
          </div>

          {(flagged > 0 || job?.flagged > 0) && !running && (
            <button type="button" onClick={() => { if (onShowFlagged) onShowFlagged(); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 13px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
                font: 'inherit', cursor: 'pointer',
                border: '1px solid rgba(224,139,47,.40)', background: '#FFF8EC',
                color: '#8A5510',
              }}>
              <AlertTriangle size={13} /> Show the flagged claims
            </button>
          )}

          {running ? (
            <button type="button" onClick={stop} disabled={busy}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
                font: 'inherit', cursor: busy ? 'not-allowed' : 'pointer',
                border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkMuted,
              }}>
              <Square size={12} /> Stop
            </button>
          ) : (
            <button type="button" onClick={start} disabled={busy}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '9px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 800,
                font: 'inherit', border: '1px solid transparent', color: '#fff',
                cursor: busy ? 'not-allowed' : 'pointer',
                background: busy ? BRAND.inkFaint : BRAND.greenDark,
              }}>
              {busy ? <Loader2 size={13} className="spin" /> : <Play size={12} />}
              {reviewed > 0 ? 'Review what has changed' : 'Start the review'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
